import React, { useState, useEffect } from 'react';
import { useAuth, SUBSCRIPTION_TIERS, TIER_FEATURES } from './AuthContext';
import { 
  User, Mail, Lock, Camera, Save, Loader, X,
  AlertCircle, CheckCircle, Crown, CreditCard,
  Bell, Shield, LogOut, ChevronRight, Eye, EyeOff,
  Check, Zap, TrendingUp, Phone, Smartphone, MessageSquare
} from 'lucide-react';
import './Auth.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const UserProfile = ({ onClose }) => {
  const { user, updateProfile, changePassword, logout, isPro, isEnterprise } = useAuth();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile form state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');

  // Notification preferences
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySMS, setNotifySMS] = useState(false);
  const [notifyApp, setNotifyApp] = useState(true);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  const currentTier = user?.subscription || SUBSCRIPTION_TIERS.FREE;
  const tierInfo = TIER_FEATURES[currentTier];

  // Load user profile on mount
  useEffect(() => {
    const loadProfile = async () => {
      const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setName(data.user.name || '');
            setEmail(data.user.email || '');
            setPhone(data.user.phone || '');
            setNotifyEmail(data.user.notify_email !== false);
            setNotifySMS(data.user.notify_sms === true);
            setNotifyApp(data.user.notify_app !== false);
          }
        }
      } catch (err) {
        console.warn('Failed to load profile:', err);
      }
    };
    loadProfile();
  }, []);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email, phone })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setIsEditing(false);
        // Also update via context if available
        if (updateProfile) {
          updateProfile({ name, email, phone });
        }
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    }
    
    setIsSaving(false);
  };

  const handleSaveNotifications = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    const token = localStorage.getItem('elemental-auth-token') || sessionStorage.getItem('elemental-auth-token');
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          notify_email: notifyEmail,
          notify_sms: notifySMS,
          notify_app: notifyApp
        })
      });
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Notification preferences saved!' });
      } else {
        const data = await res.json();
        setMessage({ type: 'error', text: data.error || 'Failed to save preferences' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    }
    
    setIsSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    const result = await changePassword(currentPassword, newPassword);
    
    setIsSaving(false);
    if (result.success) {
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to change password' });
    }
  };

  const handleLogout = () => {
    logout();
    if (onClose) onClose();
  };

  const formatPhoneDisplay = (phoneNum) => {
    if (!phoneNum) return 'Not set';
    // Format as +1 (234) 567-8901
    const cleaned = phoneNum.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1,4)}) ${cleaned.slice(4,7)}-${cleaned.slice(7)}`;
    }
    return phoneNum;
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'subscription', label: 'Subscription', icon: Crown },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell }
  ];

  return (
    <div className="profile-overlay">
      <div className="profile-modal">
        {/* Header */}
        <div className="profile-header">
          <div className="profile-header-info">
            <div className="profile-avatar">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} />
              ) : (
                <span>{user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
              )}
              <button className="profile-avatar-edit">
                <Camera size={14} />
              </button>
            </div>
            <div className="profile-header-text">
              <h2>{user?.name || 'User'}</h2>
              <p>{user?.email}</p>
              <div className="profile-tier-badge" data-tier={currentTier}>
                {currentTier === SUBSCRIPTION_TIERS.ENTERPRISE && <Zap size={12} />}
                {currentTier === SUBSCRIPTION_TIERS.PRO && <Crown size={12} />}
                <span>{tierInfo.name}</span>
              </div>
            </div>
          </div>
          <button className="profile-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`profile-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setMessage({ type: '', text: '' }); }}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="profile-content">
          {message.text && (
            <div className={`auth-alert auth-alert-${message.type}`}>
              {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              <span>{message.text}</span>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="profile-section">
              <div className="profile-section-header">
                <h3>Personal Information</h3>
                {!isEditing && (
                  <button 
                    className="profile-edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="profile-form">
                <div className="profile-field">
                  <label><User size={14} /> Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                    />
                  ) : (
                    <p>{user?.name || 'Not set'}</p>
                  )}
                </div>

                <div className="profile-field">
                  <label><Mail size={14} /> Email Address</label>
                  {isEditing ? (
                    <>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                      />
                      <span className="profile-field-hint">You'll need to log in again after changing email</span>
                    </>
                  ) : (
                    <p>{user?.email}</p>
                  )}
                </div>

                <div className="profile-field">
                  <label><Phone size={14} /> Phone Number</label>
                  {isEditing ? (
                    <>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (234) 567-8901"
                      />
                      <span className="profile-field-hint">Required for SMS alerts. Use international format.</span>
                    </>
                  ) : (
                    <p>{formatPhoneDisplay(phone)}</p>
                  )}
                </div>

                <div className="profile-field">
                  <label>Member Since</label>
                  <p>{user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}</p>
                </div>

                {isEditing && (
                  <div className="profile-actions">
                    <button 
                      className="profile-btn profile-btn-secondary"
                      onClick={() => {
                        setIsEditing(false);
                        setName(user?.name || '');
                        setEmail(user?.email || '');
                        setPhone(user?.phone || '');
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      className="profile-btn profile-btn-primary"
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                    >
                      {isSaving ? <Loader size={16} className="auth-spinner" /> : <Save size={16} />}
                      <span>Save Changes</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div className="profile-section">
              <div className="profile-section-header">
                <h3>Current Plan</h3>
              </div>

              <div className="profile-current-plan">
                <div className="profile-plan-info">
                  <div className="profile-plan-name">
                    {currentTier === SUBSCRIPTION_TIERS.ENTERPRISE && <Zap size={20} />}
                    {currentTier === SUBSCRIPTION_TIERS.PRO && <Crown size={20} />}
                    <span>{tierInfo.name}</span>
                  </div>
                  <div className="profile-plan-price">
                    {tierInfo.price === 0 ? (
                      <span>Free</span>
                    ) : (
                      <>
                        <span className="price-amount">${tierInfo.price}</span>
                        <span className="price-period">/month</span>
                      </>
                    )}
                  </div>
                </div>

                <ul className="profile-plan-features">
                  {tierInfo.features.map((feature, i) => (
                    <li key={i}>
                      <Check size={14} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {currentTier !== SUBSCRIPTION_TIERS.ENTERPRISE && (
                  <button className="profile-upgrade-btn">
                    <Crown size={16} />
                    <span>Upgrade Plan</span>
                    <ChevronRight size={16} />
                  </button>
                )}
              </div>

              {currentTier !== SUBSCRIPTION_TIERS.FREE && (
                <div className="profile-billing">
                  <h4>Billing</h4>
                  <div className="profile-billing-item">
                    <CreditCard size={18} />
                    <span>•••• •••• •••• 4242</span>
                    <button className="profile-link">Update</button>
                  </div>
                  <div className="profile-billing-item">
                    <span>Next billing date:</span>
                    <span>January 20, 2026</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="profile-section">
              <div className="profile-section-header">
                <h3>Change Password</h3>
              </div>

              <form onSubmit={handleChangePassword} className="profile-form">
                <div className="profile-field">
                  <label>Current Password</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="profile-field">
                  <label>New Password</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="profile-field">
                  <label>Confirm New Password</label>
                  <div className="profile-input-wrapper">
                    <input
                      type={showPasswords ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="profile-input-toggle"
                      onClick={() => setShowPasswords(!showPasswords)}
                    >
                      {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="profile-actions">
                  <button 
                    type="submit"
                    className="profile-btn profile-btn-primary"
                    disabled={isSaving || !currentPassword || !newPassword || !confirmPassword}
                  >
                    {isSaving ? <Loader size={16} className="auth-spinner" /> : <Lock size={16} />}
                    <span>Update Password</span>
                  </button>
                </div>
              </form>

              <div className="profile-divider" />

              <div className="profile-danger-zone">
                <h4>Danger Zone</h4>
                <button className="profile-btn profile-btn-danger" onClick={handleLogout}>
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="profile-section">
              <div className="profile-section-header">
                <h3>Alert Delivery Methods</h3>
              </div>

              <div className="profile-notifications">
                <p className="profile-notifications-desc">
                  Choose how you want to receive price alerts and notifications.
                </p>

                <label className="profile-toggle-item">
                  <div className="profile-toggle-info">
                    <div className="profile-toggle-icon">
                      <Bell size={18} />
                    </div>
                    <div>
                      <span className="profile-toggle-label">In-App Notifications</span>
                      <span className="profile-toggle-desc">See alerts in the app notification center</span>
                    </div>
                  </div>
                  <div className="profile-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifyApp}
                      onChange={(e) => setNotifyApp(e.target.checked)}
                    />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>

                <label className="profile-toggle-item">
                  <div className="profile-toggle-info">
                    <div className="profile-toggle-icon">
                      <Mail size={18} />
                    </div>
                    <div>
                      <span className="profile-toggle-label">Email Alerts</span>
                      <span className="profile-toggle-desc">Receive alerts at {user?.email}</span>
                    </div>
                  </div>
                  <div className="profile-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.checked)}
                    />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>

                <label className={`profile-toggle-item ${!phone ? 'disabled' : ''}`}>
                  <div className="profile-toggle-info">
                    <div className="profile-toggle-icon">
                      <Smartphone size={18} />
                    </div>
                    <div>
                      <span className="profile-toggle-label">SMS Alerts</span>
                      <span className="profile-toggle-desc">
                        {phone 
                          ? `Text alerts to ${formatPhoneDisplay(phone)}`
                          : 'Add a phone number in Profile to enable SMS'
                        }
                      </span>
                    </div>
                  </div>
                  <div className="profile-toggle">
                    <input 
                      type="checkbox" 
                      checked={notifySMS}
                      onChange={(e) => setNotifySMS(e.target.checked)}
                      disabled={!phone}
                    />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>

                {currentTier === SUBSCRIPTION_TIERS.FREE && (
                  <div className="profile-upgrade-note">
                    <Crown size={16} />
                    <span>Upgrade to Pro for unlimited SMS alerts</span>
                  </div>
                )}

                <div className="profile-actions" style={{ marginTop: '20px' }}>
                  <button 
                    className="profile-btn profile-btn-primary"
                    onClick={handleSaveNotifications}
                    disabled={isSaving}
                  >
                    {isSaving ? <Loader size={16} className="auth-spinner" /> : <Save size={16} />}
                    <span>Save Preferences</span>
                  </button>
                </div>
              </div>

              <div className="profile-divider" />

              <div className="profile-section-header">
                <h3>Alert Types</h3>
              </div>

              <div className="profile-notifications">
                <label className="profile-toggle-item">
                  <div className="profile-toggle-info">
                    <span className="profile-toggle-label">Price Alerts</span>
                    <span className="profile-toggle-desc">Get notified when price targets are hit</span>
                  </div>
                  <div className="profile-toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>

                <label className="profile-toggle-item">
                  <div className="profile-toggle-info">
                    <span className="profile-toggle-label">Pattern Detection</span>
                    <span className="profile-toggle-desc">Alert when chart patterns are detected</span>
                  </div>
                  <div className="profile-toggle">
                    <input type="checkbox" defaultChecked />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>

                <label className="profile-toggle-item">
                  <div className="profile-toggle-info">
                    <span className="profile-toggle-label">Weekly Summary</span>
                    <span className="profile-toggle-desc">Receive weekly market insights</span>
                  </div>
                  <div className="profile-toggle">
                    <input type="checkbox" />
                    <span className="profile-toggle-slider" />
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;